/**
 * AnamSession — Wraps @anam-ai/js-sdk for avatar rendering in BYO-audio mode.
 *
 * GPT Realtime API handles all STT/LLM/TTS. Anam is used only for:
 * - Displaying the lip-synced avatar video via WebRTC
 * - Receiving PCM audio chunks (from GPT Realtime) and playing them back
 *   with matching lip-sync via createAgentAudioInputStream
 *
 * Anam's own STT pipeline is disabled via muteInputAudio() immediately after connect.
 */

import { createClient } from '@anam-ai/js-sdk'
import type AnamClient from '@anam-ai/js-sdk/dist/module/AnamClient'
import { AnamEvent } from '@anam-ai/js-sdk'
import type { AgentAudioInputStream } from '@anam-ai/js-sdk/dist/module/types/AgentAudioInputStream'

export interface AnamSessionOptions {
  sessionToken: string
}

let videoIdCounter = 0

export class AnamSession {
  // --- Callbacks ---
  onVideoReady: ((el: HTMLVideoElement) => void) | null = null
  onConnected: (() => void) | null = null
  onDisconnected: ((reason: string) => void) | null = null
  onTalkStreamInterrupted: (() => void) | null = null
  onError: ((err: Error) => void) | null = null

  // --- Internal ---
  private client: AnamClient
  private audioInputStream: AgentAudioInputStream | null = null
  private videoElement: HTMLVideoElement | null = null
  private videoWrapper: HTMLDivElement | null = null
  private _canvas: HTMLCanvasElement | null = null
  private _canvasCtx: CanvasRenderingContext2D | null = null
  private _animFrameId = 0
  private _styleEnforceInterval: ReturnType<typeof setInterval> | null = null
  private _connected = false

  constructor(opts: AnamSessionOptions) {
    this.client = createClient(opts.sessionToken, {
      disableInputAudio: true,
      metrics: { disableClientMetrics: true },
    })
  }

  get isConnected(): boolean {
    return this._connected
  }

  get videoEl(): HTMLVideoElement | null {
    return this.videoElement
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  async connect(): Promise<void> {
    try {
      // Create a hidden video element for the avatar stream
      const videoId = `anam-avatar-video-${++videoIdCounter}`
      // Use a canvas to render the video with rounded corners.
      // WebRTC <video> in Chrome ignores all CSS clipping (border-radius,
      // overflow:hidden, clip-path) because the video compositor paints
      // directly to a hardware layer. Canvas is the only reliable approach.
      this.videoWrapper = document.createElement('div')
      this.videoWrapper.id = `anam-avatar-wrapper-${videoIdCounter}`
      this.videoWrapper.style.cssText = `
        position: fixed;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        z-index: 2147483646;
        pointer-events: none;
        display: none;
      `

      this.videoElement = document.createElement('video')
      this.videoElement.id = videoId
      this.videoElement.autoplay = true
      this.videoElement.playsInline = true
      // Video must be in the DOM and rendering for drawImage to work,
      // but we move it off-screen so it can't be seen even if Anam overrides styles
      this.videoElement.style.cssText = `
        position: fixed !important;
        left: -9999px !important;
        top: -9999px !important;
        width: 280px !important;
        height: auto !important;
        pointer-events: none !important;
      `

      const canvas = document.createElement('canvas')
      canvas.width = 280
      canvas.height = 400
      canvas.style.cssText = 'display:block;position:relative;'
      this._canvas = canvas
      this._canvasCtx = canvas.getContext('2d')!

      this.videoWrapper.appendChild(canvas)
      document.body.appendChild(this.videoWrapper)
      // Video goes on body separately (not in wrapper) so Anam can find it by ID
      document.body.appendChild(this.videoElement)

      // Enforce off-screen position every 200ms in case Anam overrides styles
      this._styleEnforceInterval = setInterval(() => {
        if (this.videoElement) {
          this.videoElement.style.cssText = `
            position: fixed !important;
            left: -9999px !important;
            top: -9999px !important;
            width: 280px !important;
            height: auto !important;
            pointer-events: none !important;
          `
        }
      }, 200)

      // Register event listeners before calling stream()
      this.client.addListener(AnamEvent.CONNECTION_ESTABLISHED, () => {
        console.log('[AnamSession] Connection established')
        this._connected = true
        this.onConnected?.()
      })

      this.client.addListener(AnamEvent.VIDEO_PLAY_STARTED, () => {
        console.log('[AnamSession] Video playing')
        if (this.videoElement) {
          // Show the wrapper and start canvas render loop
          if (this.videoWrapper) {
            this.videoWrapper.style.display = ''
          }
          this.startCanvasRender()
          this.onVideoReady?.(this.videoElement)
        }
      })

      this.client.addListener(AnamEvent.TALK_STREAM_INTERRUPTED, () => {
        console.log('[AnamSession] Talk stream interrupted')
        this.onTalkStreamInterrupted?.()
      })

      this.client.addListener(AnamEvent.CONNECTION_CLOSED, (reason) => {
        console.log('[AnamSession] Connection closed:', reason)
        this._connected = false
        this.onDisconnected?.(String(reason))
      })

      // Start streaming — attach Anam's video output to our element
      await this.client.streamToVideoElement(videoId)

      // Create the BYO audio input stream (PCM 16-bit 24kHz mono — exact GPT Realtime match)
      this.audioInputStream = this.client.createAgentAudioInputStream({
        encoding: 'pcm_s16le',
        sampleRate: 24000,
        channels: 1,
      })

      console.log('[AnamSession] BYO audio input stream ready')
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      console.error('[AnamSession] Connection failed:', error.message)
      this.onError?.(error)
      this.close()
      throw error
    }
  }

  /**
   * Send a PCM chunk from GPT Realtime to Anam for lip-sync + audio playback.
   * Accepts Uint8Array (raw PCM 16-bit little-endian, 24kHz, mono).
   */
  sendAudioChunk(pcmData: Uint8Array): void {
    this.audioInputStream?.sendAudioChunk(pcmData)
  }

  /**
   * Signal end of a speaking turn (call on response.done from GPT Realtime).
   */
  endSequence(): void {
    this.audioInputStream?.endSequence()
  }

  /**
   * Interrupt the avatar's current speech (call on user speech detected).
   */
  interrupt(): void {
    this.client.interruptPersona()
  }

  /** Draw video onto canvas with rounded top corners, every frame. */
  private startCanvasRender(): void {
    const draw = () => {
      if (!this.videoElement || !this._canvas || !this._canvasCtx) return

      const v = this.videoElement
      const c = this._canvas
      const ctx = this._canvasCtx

      // Match canvas size to video aspect ratio
      const vw = v.videoWidth || 280
      const vh = v.videoHeight || 400
      const aspect = vh / vw
      const cw = 280
      const ch = Math.round(cw * aspect)

      if (c.width !== cw || c.height !== ch) {
        c.width = cw
        c.height = ch
      }

      // Draw rounded rect clip (top corners only)
      const r = 16
      ctx.clearRect(0, 0, cw, ch)
      ctx.beginPath()
      ctx.moveTo(r, 0)
      ctx.lineTo(cw - r, 0)
      ctx.quadraticCurveTo(cw, 0, cw, r)
      ctx.lineTo(cw, ch)
      ctx.lineTo(0, ch)
      ctx.lineTo(0, r)
      ctx.quadraticCurveTo(0, 0, r, 0)
      ctx.closePath()
      ctx.clip()

      ctx.drawImage(v, 0, 0, cw, ch)

      this._animFrameId = requestAnimationFrame(draw)
    }

    this._animFrameId = requestAnimationFrame(draw)
  }

  private _closing = false

  /** Close everything. */
  close(): void {
    if (this._closing) return
    this._closing = true
    console.log('[AnamSession] Closing...')

    cancelAnimationFrame(this._animFrameId)
    this._animFrameId = 0
    if (this._styleEnforceInterval) {
      clearInterval(this._styleEnforceInterval)
      this._styleEnforceInterval = null
    }

    try { this.client.stopStreaming() } catch {}

    if (this.videoElement) {
      this.videoElement.pause()
      this.videoElement.srcObject = null
      this.videoElement = null
    }
    this._canvas = null
    this._canvasCtx = null
    if (this.videoWrapper) {
      this.videoWrapper.remove()
      this.videoWrapper = null
    }
    // Also hide/remove any other Anam elements that may exist
    document.querySelectorAll('[id^="anam-avatar-wrapper"]').forEach(el => el.remove())
    document.querySelectorAll('video[id^="anam-avatar-video"]').forEach(el => {
      ;(el as HTMLVideoElement).pause()
      el.remove()
    })

    this.audioInputStream = null
    this._connected = false
  }
}
